import type { ContentBenefit, ContentElement, ContentSchema } from '@/ts/types/schema.types'

const readObject = (value: unknown, name: string): Record<string, unknown> => {
  if (value === null || value === undefined) return {}
  if (typeof value !== 'object' || Array.isArray(value)) throw new Error(`[Partner Connect]: invalid ${name}`)
  return value as Record<string, unknown>
}

const readText = (value: unknown, name: string): string | undefined => {
  if (value === null || value === undefined) return
  if (typeof value !== 'string') throw new Error(`[Partner Connect]: invalid ${name}`)
  return value.trim() ? value : undefined
}

const addText = (elements: ContentElement[], text: string | undefined, type?: 'h1' | 'h2') => {
  if (text) {
    elements.push({
      component: 'MContent',
      config: { content_type: type ? 'Headline' : 'Text', text, type, translate: false }
    })
  }
}

export const createPartnerContentSchema = (value: unknown): ContentSchema | undefined => {
  if (value === null || value === undefined) return

  const content = readObject(value, 'content')
  const intro = readObject(content.intro, 'content.intro')
  const outro = readObject(content.outro, 'content.outro')
  const schema: ContentSchema = { intro: [], outro: [] }

  addText(schema.intro, readText(intro.headline, 'content.intro.headline'), 'h2')
  addText(schema.intro, readText(intro.subheadline, 'content.intro.subheadline'))

  if (content.benefits !== null && content.benefits !== undefined) {
    if (!Array.isArray(content.benefits)) throw new Error('[Partner Connect]: invalid content.benefits')
    const benefits: ContentBenefit[] = content.benefits.map((value, index) => {
      const name = `content.benefits[${index}]`
      const benefit = readObject(value, name)
      return {
        title: readText(benefit.title, `${name}.title`),
        text: readText(benefit.text, `${name}.text`),
        svg: readText(benefit.svg, `${name}.svg`)
      }
    })
    const visibleBenefits = benefits.filter((benefit) => benefit.title || benefit.text || benefit.svg)
    if (visibleBenefits.length) {
      schema.intro.push({ component: 'MContent', config: { content_type: 'Benefits', benefits: visibleBenefits } })
    }
  }

  addText(schema.outro, readText(outro.headline, 'content.outro.headline'), 'h2')
  const text = readText(outro.text, 'content.outro.text')
  const image = readText(outro.image, 'content.outro.image')
  if (text || image) {
    schema.outro.push({ component: 'MContent', config: { content_type: 'ImageText', text, image, translate: false } })
  }

  return schema.intro.length || schema.outro.length ? schema : undefined
}
